/**
 * Conditions validator dispatcher.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Maps each known dimension key on `path_PathGroupConditionsResource` to its
 * dedicated validator. Unknown branches round-trip and are not validated
 * (Voluum may add new dimensions; we don't reject them).
 */

import { VOLUUM_CONDITION_KEYS } from '../../voluum-conditions';
import {
  describe,
  isPlainObject,
  joinPath,
  pushError,
  type ValidationCtx,
} from '../primitives';
import {
  validateConnectionTypeCondition,
  validateDeviceTypeCondition,
  validateProxyCondition,
  validateUniqueVisitCondition,
  validateWeekdayCondition,
} from './enum-dimensions';
import {
  validateCustomVariableCondition,
  validateIpCondition,
  validateTimeOfDayCondition,
} from './nested-dimensions';
import {
  validateBrandCondition,
  validateBrowserCondition,
  validateOperatingSystemCondition,
} from './pair-array-dimensions';
import {
  validateCountryCondition,
  validateLanguageCondition,
  validateNamesPredicateCondition,
} from './string-list-dimensions';

type DimensionValidator = (
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
) => void;

const DIMENSION_VALIDATORS: Record<string, DimensionValidator> = {
  brand: validateBrandCondition,
  browser: validateBrowserCondition,
  city: validateNamesPredicateCondition,
  connectionType: validateConnectionTypeCondition,
  country: validateCountryCondition,
  customVariable: validateCustomVariableCondition,
  deviceType: validateDeviceTypeCondition,
  ip: validateIpCondition,
  isp: validateNamesPredicateCondition,
  language: validateLanguageCondition,
  mobileCarrier: validateNamesPredicateCondition,
  operatingSystem: validateOperatingSystemCondition,
  proxy: validateProxyCondition,
  referrer: validateNamesPredicateCondition,
  referrerDomain: validateNamesPredicateCondition,
  region: validateNamesPredicateCondition,
  timeOfDay: validateTimeOfDayCondition,
  uniqueVisit: validateUniqueVisitCondition,
  weekdays: validateWeekdayCondition,
};

export function validateConditionsBag(
  ctx: ValidationCtx,
  v: unknown,
  basePath: string,
): void {
  if (!isPlainObject(v)) {
    pushError(ctx, basePath, 'invalid_object', 'conditions must be an object', {
      expected: 'object',
      received: describe(v),
    });
    return;
  }
  for (const key of VOLUUM_CONDITION_KEYS) {
    const branch = v[key];
    if (branch === undefined) continue;
    const path = joinPath(basePath, key);
    if (!isPlainObject(branch)) {
      pushError(ctx, path, 'invalid_object', `"${key}" must be an object`);
      continue;
    }
    DIMENSION_VALIDATORS[key]?.(ctx, branch, path);
  }
}
